import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Send } from 'lucide-react';
import axios from 'axios';

const API = (() => {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;
  }
  return `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
})();

/**
 * RemovalRequestModal — Iter 128 (Feb 2026)
 *
 * Lightweight, public (no auth) form for owners or third parties to ask us
 * to remove a seeded/claimed business listing or correct details.  POSTs to
 * /api/listings/{businessId}/removal-request which is rate-limited at the
 * IP level (3/24h) and admin-notified.
 */
const RemovalRequestModal = ({ open, onClose, businessId, businessName, defaultType = 'removal' }) => {
  const { t } = useTranslation();
  const [type, setType] = useState(defaultType);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('owner');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/listings/${businessId}/removal-request`, {
        businessId, requesterEmail: email, requesterName: name,
        requesterRole: role, reason, type,
      });
      setResult({ ok: true, id: r.data?.requestId });
    } catch (err) {
      const msg = err?.response?.data?.detail || t('removalRequest.error', 'Could not submit — please try again later.');
      setResult({ ok: false, msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
      data-testid="removal-request-modal"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 flex items-center justify-between border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">
            {t('removalRequest.title', 'Request edit or removal')}
          </h3>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-gray-800" data-testid="removal-request-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {result?.ok ? (
          <div className="p-6 text-center" data-testid="removal-request-success">
            <p className="text-sm text-gray-800 mb-2">
              {t('removalRequest.successTitle', 'Thank you — we have received your request.')}
            </p>
            <p className="text-xs text-gray-500 mb-4">
              {t('removalRequest.successBody', 'Our team will review and reply to your email within 5 working days.')}
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium"
              data-testid="removal-request-success-close"
            >
              {t('close', 'Close')}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-4 space-y-3" data-testid="removal-request-form">
            <p className="text-xs text-gray-600">
              <span className="font-semibold">{businessName}</span>
            </p>

            <div className="flex gap-2">
              <label className={`flex-1 px-3 py-2 rounded-lg border text-sm cursor-pointer text-center
                ${type === 'removal' ? 'border-purple-500 bg-purple-50 text-purple-800' : 'border-gray-200 bg-white text-gray-700'}`}>
                <input type="radio" name="type" value="removal"
                       checked={type === 'removal'} onChange={() => setType('removal')}
                       className="sr-only" data-testid="removal-request-type-removal" />
                {t('removalRequest.typeRemoval', 'Remove listing')}
              </label>
              <label className={`flex-1 px-3 py-2 rounded-lg border text-sm cursor-pointer text-center
                ${type === 'edit' ? 'border-purple-500 bg-purple-50 text-purple-800' : 'border-gray-200 bg-white text-gray-700'}`}>
                <input type="radio" name="type" value="edit"
                       checked={type === 'edit'} onChange={() => setType('edit')}
                       className="sr-only" data-testid="removal-request-type-edit" />
                {t('removalRequest.typeEdit', 'Suggest edit')}
              </label>
            </div>

            <input
              type="text" required maxLength={120}
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder={t('removalRequest.namePh', 'Your name')}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
              data-testid="removal-request-name"
            />
            <input
              type="email" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder={t('removalRequest.emailPh', 'Your email')}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
              data-testid="removal-request-email"
            />
            <select
              value={role} onChange={(e) => setRole(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
              data-testid="removal-request-role"
            >
              <option value="owner">{t('removalRequest.roleOwner', 'I am the business owner')}</option>
              <option value="employee">{t('removalRequest.roleEmployee', 'I work for this business')}</option>
              <option value="other">{t('removalRequest.roleOther', 'Other')}</option>
            </select>
            <textarea
              required minLength={10} maxLength={2000} rows={4}
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder={t('removalRequest.reasonPh',
                type === 'removal'
                  ? 'Why should this listing be removed? (min 10 characters)'
                  : 'What should be corrected? (min 10 characters)')}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm resize-none"
              data-testid="removal-request-reason"
            />

            {result?.ok === false && (
              <p className="text-xs text-red-600" data-testid="removal-request-error">{result.msg}</p>
            )}

            <button
              type="submit" disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-purple-600 text-white text-sm font-semibold disabled:opacity-60"
              data-testid="removal-request-submit"
            >
              <Send className="h-4 w-4" />
              {submitting
                ? t('removalRequest.submitting', 'Sending…')
                : t('removalRequest.submit', 'Submit request')}
            </button>
            <p className="text-[11px] text-gray-500 leading-snug">
              {t('removalRequest.privacyNote',
                'We use your email only to follow up on this request. See our Privacy Policy.')}
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default RemovalRequestModal;
